/**
 * @file EntityFormRenderer.tsx
 * @description Shared form renderer for all entity forms (Hospital, HCP, Patient).
 * Replaces duplicated inline switch(fieldType) rendering in legacy pages.
 * Uses DynamicFormField + responsive grid layout from formLayoutUtils.
 */

import { useCallback } from 'react';
import { DynamicFormField } from '../workspace/DynamicFormField';
import type { FieldValue } from '../workspace/dynamicFormFieldUtils';
import type { FormSchema, FormFieldDefinition } from '../../types/formBuilder';
import { getGridColSpan, getFormGridContainerClass } from '../../utils/formLayoutUtils';

export interface EntityFormRendererProps {
  /** Parsed form schema */
  formSchema: FormSchema;
  /** Current form data keyed by field internalName */
  formData: Record<string, FieldValue>;
  /** Called when a field value changes */
  onChange: (fieldName: string, value: FieldValue) => void;
  /** Validation errors keyed by field internalName */
  errors?: Record<string, string>;
  /** If true, all fields are disabled */
  readOnly?: boolean;
  /** Override programId for entity selectors (when no route param available) */
  programId?: string;
  /** When set, entity selectors use public API (self-registration context) */
  publicProgramCode?: string;
}

/**
 * EntityFormRenderer renders a dynamic form from a FormSchema.
 * Uses the shared DynamicFormField component (already used by workspace pages)
 * and the 12-column responsive grid layout from formLayoutUtils.
 */
export function EntityFormRenderer({
  formSchema,
  formData,
  onChange,
  errors = {},
  readOnly = false,
  programId,
  publicProgramCode,
}: EntityFormRendererProps) {
  const handleChange = useCallback(
    (fieldName: string, value: FieldValue) => {
      if (!readOnly) {
        onChange(fieldName, value);
      }
    },
    [onChange, readOnly]
  );

  if (!formSchema?.fields || !Array.isArray(formSchema.fields)) {
    return null;
  }

  // Sort fields by sortOrder
  const sortedFields = [...formSchema.fields].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );

  const dependencyFieldKeyMap: Record<string, string> = {};
  sortedFields.forEach((field) => {
    dependencyFieldKeyMap[field.fieldId] = field.internalName;
    dependencyFieldKeyMap[field.internalName] = field.internalName;
  });

  return (
    <div className={getFormGridContainerClass()}>
      {sortedFields.map((field: FormFieldDefinition) => (
        <div
          key={field.fieldId || field.internalName}
          className={getGridColSpan(field.gridWidth)}
        >
          <DynamicFormField
            field={field}
            value={formData[field.internalName]}
            onChange={handleChange}
            error={errors[field.internalName]}
            disabled={readOnly}
            formValues={formData}
            dependencyFieldKeyMap={dependencyFieldKeyMap}
            programId={programId}
            publicProgramCode={publicProgramCode}
          />
        </div>
      ))}
    </div>
  );
}
